import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  DollarSign,
  MousePointerClick,
  UserPlus,
  CreditCard,
  Copy,
  Share2,
  TrendingUp,
  Gift,
  Wallet,
  Trophy,
  Clock,
  CheckCircle,
  Rocket,
  Users,
  BadgeDollarSign,
  Instagram,
  Youtube,
  Megaphone,
  Crown,
  Bell,
  Star,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_CONFIG = {
  bronze: { color: "text-orange-600", bg: "bg-orange-500/20", icon: "🥉" },
  silver: { color: "text-gray-400", bg: "bg-gray-500/20", icon: "🥈" },
  gold: { color: "text-yellow-500", bg: "bg-yellow-500/20", icon: "🥇" },
  platinum: { color: "text-purple-400", bg: "bg-purple-500/20", icon: "💎" }
};

export default function Referrals() {
  const { user } = useAuth();
  const [isAffiliate, setIsAffiliate] = useState(null);
  const [stats, setStats] = useState(null);
  const [tierData, setTierData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showPayout, setShowPayout] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutEmail, setPayoutEmail] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  
  // Signup form state
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [socialHandle, setSocialHandle] = useState("");
  const [promotionMethod, setPromotionMethod] = useState("");
  const [signingUp, setSigningUp] = useState(false);

  useEffect(() => {
    if (user) {
      checkAffiliateStatus();
    }
  }, [user]);

  const checkAffiliateStatus = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/status`);
      setIsAffiliate(response.data.is_affiliate);
      if (response.data.is_affiliate) {
        fetchStats();
        fetchLeaderboard();
      } else {
        setLoading(false);
      }
    } catch (error) {
      setIsAffiliate(false);
      setLoading(false);
    }
  };

  const handleAffiliateSignup = async () => {
    if (!agreeToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }
    
    setSigningUp(true);
    try {
      const response = await axios.post(`${API}/affiliate/signup`, {
        agree_to_terms: agreeToTerms,
        payout_email: signupEmail || undefined,
        social_handle: socialHandle || undefined,
        promotion_method: promotionMethod || undefined
      });
      
      toast.success(response.data.message);
      setIsAffiliate(true);
      fetchStats();
      fetchTierData();
      fetchNotifications();
      fetchLeaderboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed");
    } finally {
      setSigningUp(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/referral/stats`);
      setStats(response.data);
      fetchTierData();
      fetchNotifications();
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAffiliate(false);
      } else {
        toast.error("Failed to load referral stats");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTierData = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/tier`);
      setTierData(response.data);
    } catch (error) {
      console.error("Tier fetch failed");
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/notifications`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Notifications fetch failed");
    }
  };

  const markNotificationsRead = async () => {
    try {
      await axios.post(`${API}/affiliate/notifications/read`);
      setUnreadCount(0);
    } catch (error) {
      console.error("Mark read failed");
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/referral/leaderboard`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Leaderboard fetch failed");
    }
  };

  const copyLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      toast.success("Referral link copied!");
    }
  };

  const shareLink = async () => {
    if (navigator.share && stats?.referral_link) {
      try {
        await navigator.share({
          title: "Join Coach Coop SHRED",
          text: "Get fit with me! Use my referral link:",
          url: stats.referral_link
        });
      } catch (err) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  const requestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount < (stats?.rates?.min_payout || 10)) {
      toast.error(`Minimum payout is $${stats?.rates?.min_payout || 10}`);
      return;
    }
    if (amount > (stats?.earnings?.available || 0)) {
      toast.error("Insufficient balance");
      return;
    }

    setRequesting(true);
    try {
      const response = await axios.post(`${API}/referral/request-payout`, {
        amount,
        payout_method: "stripe",
        payout_email: payoutEmail || undefined
      });
      toast.success(response.data.message);
      setShowPayout(false);
      setPayoutAmount("");
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payout request failed");
    } finally {
      setRequesting(false);
    }
  };

  // Loading state
  if (loading || isAffiliate === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <DollarSign className="h-12 w-12 text-green-500 animate-pulse" />
      </div>
    );
  }

  // Not enrolled - show signup page
  if (!isAffiliate) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-green-500/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-5 w-5" /> Dashboard
              </Link>
              <div className="flex items-center gap-2">
                <BadgeDollarSign className="h-5 w-5 text-green-500" />
                <span className="font-bold tracking-tight">AFFILIATE PROGRAM</span>
              </div>
              <div className="w-20" />
            </div>
          </div>
        </header>

        <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Rocket className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl font-black uppercase tracking-tighter mb-2">
              Become an <span className="text-green-500">Affiliate</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Earn money by sharing Coach Coop with your audience
            </p>
          </motion.div>

          {/* Earnings Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-3 gap-3 mb-8"
          >
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
              <CardContent className="p-4 text-center">
                <MousePointerClick className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <p className="text-3xl font-black text-blue-500">$0.05</p>
                <p className="text-xs text-muted-foreground uppercase">Per Click</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
              <CardContent className="p-4 text-center">
                <UserPlus className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-black text-green-500">$1.00</p>
                <p className="text-xs text-muted-foreground uppercase">Per Signup</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-yellow-500/30">
              <CardContent className="p-4 text-center">
                <CreditCard className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-3xl font-black text-yellow-500">$10.00</p>
                <p className="text-xs text-muted-foreground uppercase">Per Sub</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wider">Why Join?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Earn passive income sharing fitness content</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Unique tracking link - get credit for every click</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Real-time dashboard to track earnings</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Fast payouts via Stripe or PayPal</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Lifetime commissions on your referrals</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Signup Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-card border-green-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-green-500" />
                  Join the Affiliate Program
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Payout Email */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Payout Email (optional)</label>
                  <Input
                    type="email"
                    placeholder="Leave blank to use account email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="bg-secondary"
                  />
                </div>
                
                {/* Social Handle */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Social Media Handle (optional)</label>
                  <Input
                    type="text"
                    placeholder="@yourhandle"
                    value={socialHandle}
                    onChange={(e) => setSocialHandle(e.target.value)}
                    className="bg-secondary"
                  />
                </div>
                
                {/* Promotion Method */}
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">How will you promote?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "social", label: "Social Media", icon: Instagram },
                      { id: "youtube", label: "YouTube", icon: Youtube },
                      { id: "blog", label: "Blog/Website", icon: Megaphone },
                      { id: "friends", label: "Friends & Family", icon: Users }
                    ].map((method) => {
                      const Icon = method.icon;
                      return (
                        <button
                          key={method.id}
                          onClick={() => setPromotionMethod(method.id)}
                          className={`p-3 rounded-lg border text-left transition-all flex items-center gap-2 ${
                            promotionMethod === method.id
                              ? 'bg-green-500/20 border-green-500'
                              : 'bg-secondary border-border hover:border-green-500/50'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="text-sm">{method.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Terms */}
                <div className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg">
                  <Checkbox
                    id="terms"
                    checked={agreeToTerms}
                    onCheckedChange={setAgreeToTerms}
                    className="mt-0.5"
                  />
                  <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer">
                    I agree to the <span className="text-primary underline">Affiliate Terms & Conditions</span>. 
                    I will not use spam, fake accounts, or misleading promotions.
                  </label>
                </div>
                
                <Button
                  onClick={handleAffiliateSignup}
                  disabled={!agreeToTerms || signingUp}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-6 text-lg"
                >
                  {signingUp ? "Creating Account..." : (
                    <>
                      <Rocket className="h-5 w-5 mr-2" /> BECOME AN AFFILIATE
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </main>
      </div>
    );
  }

  // Enrolled - show dashboard
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-green-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="font-bold tracking-tight">AFFILIATE DASHBOARD</span>
            </div>
            <button 
              onClick={() => { setShowNotifications(true); markNotificationsRead(); }}
              className="relative p-2"
            >
              <Bell className="h-5 w-5 text-muted-foreground hover:text-foreground" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-bold">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Tier Badge */}
        {tierData && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6"
          >
            <Card className={`${TIER_CONFIG[tierData.current_tier]?.bg} border-2`} style={{ borderColor: tierData.tier_info?.color }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{TIER_CONFIG[tierData.current_tier]?.icon}</span>
                    <div>
                      <p className={`text-2xl font-black uppercase ${TIER_CONFIG[tierData.current_tier]?.color}`}>
                        {tierData.current_tier} Affiliate
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {tierData.tier_info?.click_bonus > 1 && `${((tierData.tier_info.click_bonus - 1) * 100).toFixed(0)}% bonus on all earnings!`}
                        {tierData.tier_info?.click_bonus === 1 && "Earn more to unlock bonuses"}
                      </p>
                    </div>
                  </div>
                  {tierData.progress_to_next && (
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground uppercase">Next: {tierData.progress_to_next.next_tier}</p>
                      <p className="text-sm">
                        {tierData.progress_to_next.signups_needed > 0 && `${tierData.progress_to_next.signups_needed} signups`}
                        {tierData.progress_to_next.signups_needed > 0 && tierData.progress_to_next.earnings_needed > 0 && " or "}
                        {tierData.progress_to_next.earnings_needed > 0 && `$${tierData.progress_to_next.earnings_needed.toFixed(0)}`}
                      </p>
                    </div>
                  )}
                </div>
                {tierData.progress_to_next && (
                  <div className="mt-3">
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.max(tierData.progress_to_next.signups_progress, tierData.progress_to_next.earnings_progress)}%` }}
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <Gift className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-black uppercase tracking-tighter mb-2">
            Refer & <span className="text-green-500">Earn</span>
          </h1>
          <p className="text-muted-foreground">
            Share your link. Get paid for every click, signup & subscription.
          </p>
        </motion.div>

        {/* Earnings Rates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-3 mb-6"
        >
          <Card className="bg-card border-blue-500/30">
            <CardContent className="p-4 text-center">
              <MousePointerClick className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-black text-blue-500">${stats?.rates?.per_click}</p>
              <p className="text-xs text-muted-foreground uppercase">Per Click</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-green-500/30">
            <CardContent className="p-4 text-center">
              <UserPlus className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-black text-green-500">${stats?.rates?.per_signup}</p>
              <p className="text-xs text-muted-foreground uppercase">Per Signup</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-yellow-500/30">
            <CardContent className="p-4 text-center">
              <CreditCard className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-black text-yellow-500">${stats?.rates?.per_subscription}</p>
              <p className="text-xs text-muted-foreground uppercase">Per Sub</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Referral Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card border-primary mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                <Share2 className="h-4 w-4 text-primary" /> Your Referral Link
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  value={stats?.referral_link || ""}
                  readOnly
                  className="bg-secondary font-mono text-sm"
                />
                <Button onClick={copyLink} variant="outline" size="icon">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button onClick={shareLink} className="bg-primary">
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Code: <span className="font-mono font-bold text-primary">{stats?.referral_code}</span>
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <MousePointerClick className="h-5 w-5 text-blue-500 mx-auto mb-1" />
              <p className="text-3xl font-black">{stats?.stats?.unique_clicks || 0}</p>
              <p className="text-xs text-muted-foreground">Unique Clicks</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <UserPlus className="h-5 w-5 text-green-500 mx-auto mb-1" />
              <p className="text-3xl font-black">{stats?.stats?.signups || 0}</p>
              <p className="text-xs text-muted-foreground">Signups</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <CreditCard className="h-5 w-5 text-yellow-500 mx-auto mb-1" />
              <p className="text-3xl font-black">{stats?.stats?.subscriptions || 0}</p>
              <p className="text-xs text-muted-foreground">Subscriptions</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-3xl font-black">${(stats?.earnings?.total || 0).toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">Total Earned</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Earnings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/50 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider">Available Balance</p>
                  <p className="text-4xl font-black text-green-500">
                    ${(stats?.earnings?.available || 0).toFixed(2)}
                  </p>
                </div>
                <Wallet className="h-12 w-12 text-green-500/50" />
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowPayout(true)}
                  disabled={(stats?.earnings?.available || 0) < (stats?.rates?.min_payout || 10)}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-black font-bold"
                >
                  <DollarSign className="h-4 w-4 mr-2" /> Request Payout
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground mt-3 text-center">
                Minimum payout: ${stats?.rates?.min_payout || 10}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Payout Modal */}
        <AnimatePresence>
          {showPayout && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
              onClick={() => setShowPayout(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <Card className="bg-card border-green-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-green-500" />
                      Request Payout
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Amount ($)</label>
                      <Input
                        type="number"
                        placeholder={`Min $${stats?.rates?.min_payout || 10}`}
                        value={payoutAmount}
                        onChange={(e) => setPayoutAmount(e.target.value)}
                        max={stats?.earnings?.available || 0}
                        className="bg-secondary"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Available: ${(stats?.earnings?.available || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">PayPal/Stripe Email (optional)</label>
                      <Input
                        type="email"
                        placeholder="Leave blank to use account email"
                        value={payoutEmail}
                        onChange={(e) => setPayoutEmail(e.target.value)}
                        className="bg-secondary"
                      />
                    </div>
                    <Button
                      onClick={requestPayout}
                      disabled={requesting}
                      className="w-full bg-green-500 hover:bg-green-600 text-black font-bold"
                    >
                      {requesting ? "Processing..." : "Submit Payout Request"}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-card border-border mb-6">
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-500" /> Top Affiliates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {leaderboard.map((ref, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 rounded bg-secondary/50"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`font-bold ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                          #{index + 1}
                        </span>
                        <span className="font-medium">{ref.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-green-500">{ref.signups} signups</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Recent Activity */}
        {(stats?.recent_clicks?.length > 0 || stats?.recent_signups?.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {stats?.recent_signups?.map((signup, i) => (
                    <div key={`signup-${i}`} className="flex items-center justify-between p-2 rounded bg-green-500/10 border border-green-500/20">
                      <div className="flex items-center gap-2">
                        <UserPlus className="h-4 w-4 text-green-500" />
                        <span className="text-sm">New signup!</span>
                      </div>
                      <span className="text-green-500 font-bold">+${signup.earnings}</span>
                    </div>
                  ))}
                  {stats?.recent_clicks?.slice(0, 5).map((click, i) => (
                    <div key={`click-${i}`} className="flex items-center justify-between p-2 rounded bg-blue-500/10 border border-blue-500/20">
                      <div className="flex items-center gap-2">
                        <MousePointerClick className="h-4 w-4 text-blue-500" />
                        <span className="text-sm">{click.is_unique ? 'Unique click' : 'Click'}</span>
                      </div>
                      <span className={`font-bold ${click.is_unique ? 'text-blue-500' : 'text-muted-foreground'}`}>
                        {click.is_unique ? `+$${click.earnings}` : '$0'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Notifications Modal */}
        <AnimatePresence>
          {showNotifications && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-background/80 backdrop-blur-sm p-4"
              onClick={() => setShowNotifications(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: -20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: -20 }}
                className="w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Notifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {notifications.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No notifications yet</p>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {notifications.map((notif, i) => (
                          <div
                            key={notif.id || i}
                            className={`p-3 rounded-lg border ${notif.read ? 'bg-secondary/30 border-border' : 'bg-primary/10 border-primary/30'}`}
                          >
                            <p className="text-sm">{notif.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
